import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Instagram, Twitter, Youtube, Linkedin, MapPin, ArrowUpRight, Phone, Mail } from "lucide-react";
import { CONTACT } from "@/constants/contact";

const SOCIALS = [
  { Icon: Instagram, href: "https://www.instagram.com/ebikego/", label: "Instagram" },
  { Icon: Twitter, href: "https://twitter.com/ebikego", label: "Twitter" },
  { Icon: Youtube, href: "https://www.youtube.com/@ebikego", label: "YouTube" },
  { Icon: Linkedin, href: "https://in.linkedin.com/company/ebikego", label: "LinkedIn" },
];

const EBG_LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_ebikego-preview/artifacts/id92axz4_EBG%20Logo.png";

export const Footer = () => (
  <footer className="bg-[#0A0B10] text-white" data-testid="main-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo dark />
          <p className="text-white/60 text-sm mt-5 max-w-xs leading-relaxed">
            India&apos;s trusted electric two-wheeler rental platform. Ride electric. Ride smarter.
          </p>
          <div className="flex gap-3 mt-6">
            {SOCIALS.map(({ Icon, href, label }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`social-${label.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-white/10 hover:border-[#3DDC97] hover:text-[#3DDC97] flex items-center justify-center transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>

          {/* EBG Group Company Card */}
          <a
            href="https://theebg.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ebg-group-card"
            className="group mt-8 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0F0F14] hover:border-[#B8860B]/50 px-4 py-3.5 transition-colors max-w-xs w-full"
          >
            <img src={EBG_LOGO} alt="EBG" className="h-10 w-auto object-contain shrink-0" />
            <div className="min-w-0">
              <p className="font-display font-semibold text-white text-sm leading-tight">
                An <span style={{ color: "#D4AF37" }}>EBG</span> Group Company
              </p>
              <p className="text-xs text-white/50 mt-1 flex items-center gap-1 group-hover:text-white/80">
                theebg.com
                <ArrowUpRight size={11} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </p>
            </div>
          </a>

          {/* Contact block */}
          <div className="mt-6 space-y-3 max-w-xs text-sm">
            <a
              href={`tel:+91${CONTACT.phone}`}
              data-testid="footer-phone"
              className="flex items-center gap-3 text-white/70 hover:text-[#3DDC97] transition-colors"
            >
              <Phone size={14} className="shrink-0" />
              <span className="font-mono-num">{CONTACT.phoneDisplay}</span>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="footer-email"
              className="flex items-center gap-3 text-white/70 hover:text-[#3DDC97] transition-colors"
            >
              <Mail size={14} className="shrink-0" />
              <span>{CONTACT.email}</span>
            </a>
            <div className="flex items-start gap-3 text-white/60 leading-relaxed" data-testid="footer-address">
              <MapPin size={14} className="shrink-0 mt-1" />
              <span>{CONTACT.address}</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm mb-4 text-white/90">Rent EV</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/bikes?category=electric-bicycle" className="hover:text-[#3DDC97]">Electric Bicycles</Link></li>
            <li><Link to="/bikes?category=low-speed-scooter" className="hover:text-[#3DDC97]">Low Speed EV Scooters</Link></li>
            <li><Link to="/bikes?category=high-speed-scooter" className="hover:text-[#3DDC97]">High Speed EV Scooters</Link></li>
            <li><Link to="/bikes?category=electric-motorcycle" className="hover:text-[#3DDC97]">Electric Motorcycles</Link></li>
            <li><Link to="/business-fleet" className="hover:text-[#3DDC97]">Business & Fleet Rentals</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm mb-4 text-white/90">Cities</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/bikes?city=bangalore" className="hover:text-[#3DDC97]">Bangalore</Link></li>
            <li><Link to="/bikes?city=hyderabad" className="hover:text-[#3DDC97]">Hyderabad</Link></li>
            <li><Link to="/bikes?city=chennai" className="hover:text-[#3DDC97]">Chennai</Link></li>
            <li><Link to="/bikes?city=mumbai" className="hover:text-[#3DDC97]">Mumbai</Link></li>
            <li><Link to="/bikes?city=delhi" className="hover:text-[#3DDC97]">Delhi</Link></li>
            <li><Link to="/cities" className="hover:text-[#3DDC97]">All cities</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm mb-4 text-white/90">Company</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/about" className="hover:text-[#3DDC97]">About eBikeGo</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#3DDC97]">How it works</Link></li>
            <li><Link to="/contact" className="hover:text-[#3DDC97]">Contact Us</Link></li>
            <li><Link to="/support" className="hover:text-[#3DDC97]">Customer Support</Link></li>
            <li><Link to="/faqs" className="hover:text-[#3DDC97]">FAQs</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm mb-4 text-white/90">Policies</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/rental-terms" className="hover:text-[#3DDC97]">Rental Terms</Link></li>
            <li><Link to="/privacy" className="hover:text-[#3DDC97]">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-[#3DDC97]">Cancellation & Refund</Link></li>
            <li><Link to="/insurance-policy" className="hover:text-[#3DDC97]">Insurance & Damage</Link></li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-white/40 font-mono-num">© 2026 THINK EBIKEGO PRIVATE LIMITED. ALL RIGHTS RESERVED.</p>
        <div className="flex items-center gap-2 text-xs text-white/40">
          <MapPin size={12} />
          <span>Available in Mumbai, Delhi, Bangalore, Hyderabad, Chennai</span>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-white/5 text-center">
        <p className="text-xs text-white/40" data-testid="reachvel-credit">
          Design and Development by{" "}
          <a
            href="https://www.reachvel.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reachvel-link"
            className="font-semibold text-white/70 hover:text-[#3DDC97] transition-colors underline-offset-2 hover:underline"
          >
            Reachvel
          </a>
        </p>
      </div>
    </div>
  </footer>
);
